import { WebSocketProvider } from '@flik/shared';
import {
  DEFAULT_AUTH_OPTIONS,
  getUri,
  ReactQueryProvider,
} from '@flik/shared/api';
import { IdentityProvider, ThemeProvider } from '@flik/shared/components';
import React, { useEffect, useState } from 'react';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate,
} from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import InvitationRedirectHandler from './components/InvitationRedirectHandler';
import SessionInvitationHandler from './components/SessionInvitationHandler';
import SessionInvitationModal, {
  SessionInvitation,
} from './components/SessionInvitationModal';
import SessionRefreshHandler from './components/SessionRefreshHandler';
import {
  SessionInvitationProvider,
  useSessionInvitation,
} from './contexts/SessionInvitationContext';
import ActiveSessionPage from './pages/ActiveSessionPage';
import AuthEmailConfirmedPage from './pages/AuthEmailConfirmedPage';
import ConfirmCreateOrganizationPage from './pages/ConfirmCreateOrganizationPage';
import GetStartedPage from './pages/GetStartedPage';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';
import OrganizationManagementPage from './pages/OrganizationManagementPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import SessionPage from './pages/SessionPage';
import SignInPage from './pages/SignInPage';
import SignInV2Page from './pages/SignInV2Page';
import { loadWebTheme } from './theme';

// Mock components for web app (since desktop components aren't available)
const MockSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <div>{children}</div>;
};

// Component to handle invitation acceptance and navigation
const InvitationHandler: React.FC = () => {
  const navigate = useNavigate();
  const {
    currentInvitation,
    isModalOpen,
    acceptInvitation,
    declineInvitation,
  } = useSessionInvitation();

  const handleAcceptInvitation = (invitation: SessionInvitation) => {
    // Navigate directly to the active session page
    navigate(`/sessions/${invitation.sessionId}/active`);
    acceptInvitation(invitation);
  };

  return (
    <>
      <SessionInvitationHandler />
      <SessionRefreshHandler />
      <SessionInvitationModal
        invitation={currentInvitation}
        isOpen={isModalOpen}
        onAccept={handleAcceptInvitation}
        onDecline={declineInvitation}
      />
    </>
  );
};

// Component that applies theme background to the main container
const ThemedContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div
      className="w-full min-h-screen flex flex-col m-0 p-0 font-sans antialiased"
      style={{ backgroundColor: 'var(--page-bg)' }}
    >
      {children}
    </div>
  );
};

// Mock hooks (not used in this simplified version)
// const useIdentityContext = () => ({
//   isLoggedIn: true,
//   isLoading: false,
//   signOut: () => {},
// });

// const useSessionState = () => ({
//   hasSession: false,
// });

// const useThemeContext = () => ({
//   theme: 'dark',
// });

function App() {
  const [themeRegistry, setThemeRegistry] = useState<any>({});
  const [themeLoaded, setThemeLoaded] = useState(false);

  // Load theme configuration at runtime
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const registry = await loadWebTheme();
        setThemeRegistry(registry);
        setThemeLoaded(true);
      } catch (error) {
        console.error('Failed to load theme:', error);
        // Use fallback theme
        setThemeLoaded(true);
      }
    };

    loadTheme();
  }, []);

  // Don't render until theme is loaded
  if (!themeLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black text-white">
        <div>Loading theme...</div>
      </div>
    );
  }

  // Create storage functions - use localStorage for web app
  const createStorage = () => {
    return {
      get: (key: string): string | null => {
        return localStorage.getItem(key);
      },
      set: (key: string, value: string): void => {
        localStorage.setItem(key, value);
      },
      remove: (key: string): void => {
        localStorage.removeItem(key);
      },
    };
  };

  const authOptions = {
    ...DEFAULT_AUTH_OPTIONS,
    storage: createStorage(),
  };

  return (
    <IdentityProvider authOptions={authOptions}>
      <ReactQueryProvider uri={getUri} auth={authOptions}>
        <WebSocketProvider>
          <SessionInvitationProvider>
            <MockSessionProvider>
              <ThemeProvider
                initialTheme="dark"
                initialRegistry={themeRegistry}
              >
                <Router>
                  <ThemedContainer>
                    <InvitationHandler />
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <AuthGuard>
                            <HomePage />
                          </AuthGuard>
                        }
                      />
                      <Route path="/get-started" element={<GetStartedPage />} />
                      <Route
                        path="/organization/confirm"
                        element={<ConfirmCreateOrganizationPage />}
                      />
                      <Route path="/signin" element={<SignInPage />} />
                      <Route path="/signin-v2" element={<SignInV2Page />} />
                      <Route
                        path="/profile/setup"
                        element={<ProfileSetupPage />}
                      />
                      <Route
                        path="/auth/email/confirmed"
                        element={<AuthEmailConfirmedPage />}
                      />
                      <Route
                        path="/sessions/invitations/:invitationToken"
                        element={<InvitationRedirectHandler />}
                      />
                      <Route
                        path="/sessions/:sessionId"
                        element={
                          <AuthGuard>
                            <SessionPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/sessions/:sessionId/active"
                        element={
                          <AuthGuard>
                            <ActiveSessionPage />
                          </AuthGuard>
                        }
                      />
                      <Route
                        path="/organization/manage"
                        element={
                          <AuthGuard>
                            <OrganizationManagementPage />
                          </AuthGuard>
                        }
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </ThemedContainer>
                </Router>
              </ThemeProvider>
            </MockSessionProvider>
          </SessionInvitationProvider>
        </WebSocketProvider>
      </ReactQueryProvider>
    </IdentityProvider>
  );
}

export default App;
